
import { Component, DestroyRef, OnInit, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ButtonComponent, DialogService, EmptyComponent, InputComponent } from 'nebula-ui-kit';
import {
  FieldLabelComponent,
  GridFormComponent,
  GridFormItemComponent,
  PageHeaderAction,
  PageHeaderComponent,
} from '@nebula-shared/ui';
import { finalize, map, timeout } from 'rxjs';
import { IngestProjectService } from '../../../../../data-access/src/lib/ingest/ingest-project.service';
import type { IngestProject, IngestSubfolder } from '../../../../../domain/src/lib/ingest/ingest-project.model';
import type { ExtendedIngestProjectDetailStore } from '../../../../../domain/src/lib/ingest/ingest-store.model';
import { ingestProjectDetailStore } from '../../../../../store/src/lib/ingest-project-detail.store';
import { INGEST_MODULE_ENV } from '@nebula/shared-vault/data-access';
import { getMockSubfolders, resolvePreviewProject } from '../ingest-ui-mock.data';
import {
  ingestInactiveProjectMessage,
  isIngestProjectEligibleForUpload,
} from '../ingest-project-active.util';
import { openIngestAlertDialog } from '../ingest-dialog.util';
import { messageFromIngestHttpError } from '../ingest-http-error.util';
import {
  formatIngestSubfolderDisplay,
  mergeIngestSubfolderLists,
  normalizeIngestSubfolder,
  normalizeIngestSubfolderNameInput,
  normalizeIngestSubfolders,
  validateIngestSubfolderName,
} from '../ingest-subfolder.util';
import { ingestUiStore } from '../../../../../store/src/lib/ingest-ui.store';

@Component({
  selector: 'app-ingest-project-detail',
  standalone: true,
  imports: [
    ButtonComponent,
    EmptyComponent,
    InputComponent,
    PageHeaderComponent,
    FieldLabelComponent,
    GridFormComponent,
    GridFormItemComponent
],
  templateUrl: './ingest-project-detail.component.html',
  styleUrls: ['./ingest-project-detail.component.scss'],
})
export class IngestProjectDetailComponent implements OnInit {
  private readonly ingestProjectService = inject(IngestProjectService);
  private readonly ingestEnv = inject(INGEST_MODULE_ENV);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly uiStore = inject(ingestUiStore);
  private readonly dialogService = inject(DialogService);

  readonly detailStore = inject(ingestProjectDetailStore) as unknown as ExtendedIngestProjectDetailStore;

  newSubfolderName = '';

  readonly formatIngestSubfolderDisplay = formatIngestSubfolderDisplay;

  readonly headerActions = computed<PageHeaderAction[]>(() => {
    const project = this.detailStore.project();
    const actions: PageHeaderAction[] = [
      {
        id: 'back',
        label: 'Volver',
        icon: 'ArrowLeft',
        variant: 'secondary',
      },
    ];
    if (project && isIngestProjectEligibleForUpload(project)) {
      actions.push({
        id: 'upload',
        label: 'Subir archivos y carpetas',
        icon: 'Upload',
        variant: 'primary',
      });
    }
    return actions;
  });

  readonly canModifyProject = computed(() => {
    const project = this.detailStore.project();
    return !!project && isIngestProjectEligibleForUpload(project);
  });

  readonly inactiveProjectMessage = computed(() => {
    const project = this.detailStore.project();
    if (!project || isIngestProjectEligibleForUpload(project)) {
      return null;
    }
    return ingestInactiveProjectMessage(project.name);
  });

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((p) => Number(p.get('id'))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((id) => this.loadProject(id));
  }

  private loadProject(id: number): void {
    if (!Number.isFinite(id) || id < 1) {
      this.detailStore.setLoading(false);
      this.detailStore.setProject(null);
      this.detailStore.setSubfolders([]);
      this.detailStore.setSubfoldersError(null);
      this.detailStore.setError('Identificador de expediente no válido.');
      return;
    }

    this.detailStore.resetForNavigation();
    const saved = this.uiStore.detailDraftByProject()[String(id)];
    this.newSubfolderName = saved?.newSubfolderName ?? '';

    if (this.ingestEnv.previewMode) {
      const mock = resolvePreviewProject(id);
      if (!mock) {
        this.detailStore.setLoading(false);
        this.detailStore.setError('Expediente no encontrado (vista prevía).');
        return;
      }
      this.detailStore.setProject(mock);
      this.detailStore.setLoading(false);
      this.loadSubfolders();
      return;
    }

    this.ingestProjectService
      .getById(id)
      .pipe(
        timeout(45000),
        finalize(() => {
          this.detailStore.setLoading(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (p: IngestProject) => {
          this.detailStore.setProject(p);
          this.detailStore.setLoading(false);
          this.loadSubfolders();
        },
        error: (err) => {
          this.detailStore.setProject(null);
          this.detailStore.setLoading(false);
          this.detailStore.setError(
            err?.error?.detail || err?.message || 'No se pudo cargar el expediente.',
          );
        },
      });
  }

  loadSubfolders(created?: IngestSubfolder): void {
    const project = this.detailStore.project();
    if (!project) return;

    this.detailStore.setLoadingSubfolders(true);
    this.detailStore.setSubfoldersError(null);

    if (this.ingestEnv.previewMode) {
      this.detailStore.setSubfolders(getMockSubfolders(project.id));
      this.detailStore.setLoadingSubfolders(false);
      return;
    }

    this.ingestProjectService
      .getSubfolders(project.id)
      .pipe(
        timeout(30000),
        finalize(() => {
          this.detailStore.setLoadingSubfolders(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          const rows = mergeIngestSubfolderLists(
            normalizeIngestSubfolders(items ?? []),
            created,
          );
          this.detailStore.setSubfolders(rows);
        },
        error: (err) => {
          this.detailStore.setSubfolders([]);
          const message = messageFromIngestHttpError(
            err,
            'No se pudieron cargar subcarpetas.',
          );
          this.detailStore.setSubfoldersError(message);
          openIngestAlertDialog(this.dialogService, {
            title: 'Error al cargar subcarpetas',
            message,
            variant: 'error',
          });
        },
      });
  }

  createSubfolder(): void {
    const project = this.detailStore.project();
    if (!project || !isIngestProjectEligibleForUpload(project)) {
      this.detailStore.setSubfoldersError(
        ingestInactiveProjectMessage(project?.name),
      );
      return;
    }

    const name = normalizeIngestSubfolderNameInput(this.newSubfolderName).trim();
    const nameError = validateIngestSubfolderName(name);
    if (nameError) {
      this.detailStore.setSubfoldersError(nameError);
      return;
    }

    this.detailStore.setCreatingSubfolder(true);
    this.detailStore.setSubfoldersError(null);

    if (this.ingestEnv.previewMode) {
      this.detailStore.setSubfolders([
        ...this.detailStore.subfolders(),
        { path: name, display_name: name },
      ]);
      this.newSubfolderName = '';
      this.detailStore.setCreatingSubfolder(false);
      return;
    }

    this.ingestProjectService
      .createSubfolder(project.id, { name })
      .pipe(
        timeout(30000),
        finalize(() => {
          this.detailStore.setCreatingSubfolder(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (created) => {
          this.newSubfolderName = '';
          this.uiStore.clearDetailDraftForProject(project.id);
          this.loadSubfolders(normalizeIngestSubfolder(created));
        },
        error: (err) => {
          this.detailStore.setSubfoldersError(
            messageFromIngestHttpError(err, 'No se pudo crear la subcarpeta.'),
          );
        },
      });
  }

  goBack(): void {
    void this.router.navigate(['projects'], {
      relativeTo: this.route.parent,
    });
  }

  goToUpload(): void {
    const project = this.detailStore.project();
    if (!project || !isIngestProjectEligibleForUpload(project)) return;
    void this.router.navigate(['projects', project.id, 'upload'], {
      relativeTo: this.route.parent,
    });
  }

  onHeaderAction(actionId: string): void {
    if (actionId === 'back') {
      this.goBack();
      return;
    }
    if (actionId === 'upload') {
      this.goToUpload();
    }
  }

  onNewSubfolderNameChange(value: string): void {
    this.newSubfolderName = normalizeIngestSubfolderNameInput(value);
    const project = this.detailStore.project();
    if (!project) return;
    this.uiStore.setDetailDraftForProject(project.id, {
      newSubfolderName: this.newSubfolderName,
    });
  }
}
